import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { getProjects, staticImg } from "@/lib/api";
import { REVEAL_INITIAL_LARGE, REVEAL_ANIMATE, CARD_TRANSITION_BASE } from "@/lib/motion";

export default function ProjectsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    getProjects().then(setItems).catch(() => setItems([]));
  }, []);

  const filters = [
    { key: "all", label: t.projects.filters.all },
    { key: "cuisine", label: t.projects.filters.cuisine },
    { key: "bain", label: t.projects.filters.bain },
    { key: "dressing", label: t.projects.filters.dressing },
    { key: "espace", label: t.projects.filters.espace },
  ];

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((p) => p.category === filter);
  }, [items, filter]);

  return (
    <div data-testid="projects-page">
      {/* HERO */}
      <Section testid="projects-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{t.nav.projects}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6">
                {t.projects.hero_h1}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.projects.hero_sub}
              </p>
            </Reveal>
          </div>

          {/* Filters */}
          <Reveal delay={0.3}>
            <div className="mt-14 flex flex-wrap gap-2" data-testid="projects-filters">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`px-5 py-2.5 rounded-full border text-[11px] tracking-[0.22em] uppercase font-medium transition-all duration-300 ${
                    filter === f.key
                      ? "bg-arven-gold text-arven-noir border-arven-gold"
                      : "bg-transparent text-arven-offwhite/80 border-white/15 hover:border-arven-gold/60 hover:text-arven-offwhite"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* GRID */}
      <Container className="pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={REVEAL_INITIAL_LARGE}
              animate={REVEAL_ANIMATE}
              transition={{ ...CARD_TRANSITION_BASE, delay: 0.05 * i }}
            >
              <Link
                to={`/projets/${p.slug}`}
                className="group block overflow-hidden rounded-2xl kbd-card transition-all duration-500"
                data-testid={`project-card-${p.slug}`}
              >
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img
                    src={staticImg(p.image)}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-arven group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-arven-noir/95 via-arven-noir/30 to-transparent" />
                  <div className="absolute inset-0 p-6 md:p-7 flex flex-col justify-end">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-arven-gold mb-2">
                      {p.city} — {p.country}
                    </p>
                    <h3 className="serif text-2xl md:text-[28px] leading-tight">
                      {lang === "fr" ? p.title : p.title_en}
                    </h3>
                    <div className="mt-3 flex items-center text-[11px] tracking-wider text-arven-offwhite/65">
                      <span>{p.year}</span>
                      <span className="mx-3 h-px w-6 bg-arven-gold/60" />
                      <span>{p.surface}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-arven-offwhite/50 py-20">No projects in this category yet.</p>
        )}
      </Container>

      {/* CTA */}
      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight">{t.projects.cta}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center">
                <Link to="/rendez-vous" className="btn-gold">
                  {t.common.appointment} <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
